import {
  IOption,
  RevokeApplicationStatus,
  SubmitApplicationStatus,
  TFormatFn,
  TOptionValue,
  TTableFilterPayload,
  TSelectorFn,
  TStatusOwner,
} from '../types.ts';
import React from 'react';
import { REVOKE_STATUS_CONFIG, SUBMIT_STATUS_CONFIG } from '../constants.ts';
import { buildSortQuery, TServerSort } from './sorting.ts';

const getStatusConfig = (status?: string, owner: TStatusOwner = 'submit') => {
  if (!status) {
    return undefined;
  }

  if (owner === 'submit') {
    return SUBMIT_STATUS_CONFIG[status as SubmitApplicationStatus];
  }

  return REVOKE_STATUS_CONFIG[status as RevokeApplicationStatus];
};

export const getStatusText = (status?: string, owner: TStatusOwner = 'submit'): string => {
  const config = getStatusConfig(status, owner);
  return config?.text || status || 'Невідомо';
};

export const getStatusTooltip = (status?: string, owner: TStatusOwner = 'submit'): string | null => {
  const config = getStatusConfig(status, owner);
  return config?.tooltip || null;
};

export const getDenyReason = (denyType: number | undefined, denyReason?: string): string => {
  if (denyType === undefined) return 'Причина не вказана';

  const reasons: Record<number, string> = {
    0: 'Не погоджуюсь з метою обробки моїх персональних даних',
    1: 'Не погоджуюсь із обсягом моїх персональних даних, який оброблятиметься Банком',
    2: denyReason || 'Інша причина',
  };

  return reasons[denyType] || 'Причина не вказана';
};

export const getUniqueOptions = <T, V extends TOptionValue, L = V>(
  data: T[],
  selectorFn: TSelectorFn<T, V>,
  formatFn: TFormatFn<V, L> = (v) => v as unknown as L
): IOption<V, L>[] => {
  const rawValues = data.map(selectorFn);

  const uniqueValues = [...new Set(rawValues)]
    .filter((val): val is V => !!val)
    .sort((a, b) => {
      if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b);
      }
      return (a as number) - (b as number);
    });

  return uniqueValues.map((value) => ({
    label: formatFn(value),
    value: value,
  }));
};

export const getSafeUniqueOptions = <T>(
  content: T[] | undefined,
  getter: (item: T) => string
): { value: string; label: string }[] => {
  if (!content || !Array.isArray(content)) {
    return [];
  }
  return getUniqueOptions(content, getter);
};

export const getNextOrder = (
  currentOrder?: 'ascend' | 'descend'
): 'ascend' | 'descend' | undefined => {
  if (currentOrder === 'ascend') return 'descend';
  if (currentOrder === 'descend') return undefined;
  return 'ascend';
};

export const getSelectedKeyValue = (selectedKeys: React.Key[]): string => {
  if (selectedKeys.length === 0) {
    return OPTION_ALL.value;
  }
  const firstKey = selectedKeys[0];
  return String(firstKey);
};

export const buildTablePayload = (
  page: number,
  pageSize: number,
  filters: TTableFilterPayload,
  sort: TServerSort
): Record<string, unknown> => {
  const sortQuery = buildSortQuery(sort);

  return {
    ...filters,
    page,
    size: pageSize,
    ...(sortQuery ? { sort: sortQuery } : {}),
  };
};

const getSortableValue = (record: object, fieldName: string): string =>
  String((record as Record<string, unknown>)[fieldName] ?? '');

export const safeStringSorter = <TRecord extends object>(fieldName: string) => (
  a: TRecord,
  b: TRecord
) => {
  const aValue = getSortableValue(a, fieldName);
  const bValue = getSortableValue(b, fieldName);
  return aValue.localeCompare(bValue);
};

export const SUBMIT_STATUS_FILTER_OPTIONS: Array<{ label: string; value: SubmitApplicationStatus }> = [
  {
    label: SUBMIT_STATUS_CONFIG[SubmitApplicationStatus.SENT].text,
    value: SubmitApplicationStatus.SENT,
  },
  {
    label: SUBMIT_STATUS_CONFIG[SubmitApplicationStatus.VERIFIED].text,
    value: SubmitApplicationStatus.VERIFIED,
  },
  {
    label: SUBMIT_STATUS_CONFIG[SubmitApplicationStatus.EXPIRED].text,
    value: SubmitApplicationStatus.EXPIRED,
  },
  {
    label: SUBMIT_STATUS_CONFIG[SubmitApplicationStatus.DENIED].text,
    value: SubmitApplicationStatus.DENIED,
  },
  {
    label: SUBMIT_STATUS_CONFIG[SubmitApplicationStatus.ACCEPTED].text,
    value: SubmitApplicationStatus.ACCEPTED,
  },
];

export const REVOKE_STATUS_FILTER_OPTIONS: Array<{ label: string; value: RevokeApplicationStatus }> = [
  {
    label: REVOKE_STATUS_CONFIG[RevokeApplicationStatus.NEW].text,
    value: RevokeApplicationStatus.NEW,
  },
  {
    label: REVOKE_STATUS_CONFIG[RevokeApplicationStatus.VERIFIED].text,
    value: RevokeApplicationStatus.VERIFIED,
  },
  {
    label: REVOKE_STATUS_CONFIG[RevokeApplicationStatus.PENDING].text,
    value: RevokeApplicationStatus.PENDING,
  },
  {
    label: REVOKE_STATUS_CONFIG[RevokeApplicationStatus.EXPIRED].text,
    value: RevokeApplicationStatus.EXPIRED,
  },
  {
    label: REVOKE_STATUS_CONFIG[RevokeApplicationStatus.COMPLETED].text,
    value: RevokeApplicationStatus.COMPLETED,
  },
];

export const OPTION_ALL = {
  label: 'Всі',
  value: 'all',
};
